import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

/** Парный к `InlineNumberInput`, но для строки. */
export function InlineTextInput({
  value,
  onCommit,
  disabled,
  busy,
  ariaLabel,
  width = 200,
  invalid,
  placeholder,
  required = true,
}: {
  value: string
  onCommit: (next: string) => void | Promise<void>
  disabled?: boolean
  busy?: boolean
  ariaLabel?: string
  width?: number
  invalid?: boolean
  placeholder?: string
  required?: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value)
  const [localError, setLocalError] = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)
  const focused = useRef(false)

  useEffect(() => {
    if (focused.current) return
    // Локальный буфер пересинхронизируется с пропом, когда поле не в фокусе
    // (легитимный pattern «sync external value with local state»).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft(value)
  }, [value])

  function tryCommit() {
    const trimmed = draft.trim()
    if (required && !trimmed) {
      setLocalError(t('validate.fillField'))
      return
    }
    if (trimmed === value) return
    setLocalError(null)
    void onCommit(trimmed)
  }

  return (
    <div className="inline-flex flex-col">
      <input
        ref={ref}
        type="text"
        disabled={disabled || busy}
        aria-label={ariaLabel}
        aria-invalid={invalid || localError != null || undefined}
        placeholder={placeholder}
        value={draft}
        style={{ width }}
        onFocus={() => {
          focused.current = true
        }}
        onBlur={() => {
          focused.current = false
          tryCommit()
        }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            ref.current?.blur()
          } else if (e.key === 'Escape') {
            setDraft(value)
            setLocalError(null)
            ref.current?.blur()
          }
        }}
        className={clsx(
          'stryzh-input-field',
          (invalid || localError) && 'stryzh-input-field--error',
        )}
      />
      {localError ? (
        <span className="stryzh-input-error-text mt-1 text-[length:var(--stryzh-font-size-xs)]">
          {localError}
        </span>
      ) : null}
    </div>
  )
}
